import React from 'react'

import { isNullOrUndefined, unionSortedHighlights } from '../../utils'

import HighlightedText from './Highlight'

/**
 * TextHighlights component for displaying text with highlights.
 *
 * @see https://github.com/aws-samples/aws-kendra-transcribe-media-search/blob/main/finderapp/src/search/resultsPanel/components/TextWithHighlight.tsx TextWithHighlight.tsx
 * @example
 * const textWithHighlights = { Text: "Sample text", Highlights: [{ BeginOffset: 0, EndOffset: 6 }] }
 * return <TextHighlights textWithHighlights={textWithHighlights} />
 *
 * @param {Object} props - The component props.
 * @param {string} props.className - The CSS class name for the component.
 * @param {Object} props.textWithHighlights - The text with highlights.
 * @param {string} props.textWithHighlights.Text - The text to be highlighted.
 * @param {Array} props.textWithHighlights.Highlights - The highlights information.
 * @param {number} props.textWithHighlights.Highlights[].BeginOffset - The start index of the highlight.
 * @param {number} props.textWithHighlights.Highlights[].EndOffset - The end index of the highlight.
 * @returns {JSX.Element|null} The rendered TextHighlights component or null if textWithHighlights is null or undefined.
 */
export default class TextHighlights extends React.Component {
  render() {
    const { className, textWithHighlights } = this.props

    if (isNullOrUndefined(textWithHighlights)) {
      return null
    }

    const text = textWithHighlights.Text
    if (isNullOrUndefined(textWithHighlights.Highlights)) {
      return <span>{text}</span>
    }

    // TopAnswer will be out of order, sort the highlights
    const sortedHighlights = unionSortedHighlights(
      textWithHighlights.Highlights?.sort(
        (highlight1, highlight2) =>
          highlight1.BeginOffset - highlight2.BeginOffset
      )
    )
    const lastHighlight = sortedHighlights[sortedHighlights.length - 1]

    return (
      <span className={className}>
        {sortedHighlights.map((highlight, idx) => (
          <span key={idx}>
            <span>
              {text?.substring(
                idx === 0 ? 0 : sortedHighlights[idx - 1].EndOffset,
                highlight.BeginOffset
              )}
            </span>
            <HighlightedText text={text} highlight={highlight} />
          </span>
        ))}
        <span>
          {text?.substring(lastHighlight ? lastHighlight.EndOffset : 0)}
        </span>
      </span>
    )
  }
}
